import { type ChangeEvent, useEffect, useRef } from 'react';
import { z } from 'zod/v3';
import { Input } from '@/components/shadcn/input.tsx';
import { Label } from '@/components/shadcn/label.tsx';
import { RadioGroup, RadioGroupItem } from '@/components/shadcn/radio-group.tsx';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select';
import {
  govtIntakeFormContactMethod,
  govtIntakeFormDepartments,
  govtIntakeFormOrganizationType,
  govtIntakeFormState,
} from '@/data/govtIntakeStaticData.ts';
import type { ValidationChange } from '@/modules/governmentIntake/base-entity.tsx';
import { FormField } from '@/modules/governmentIntake/components/form-sections/form-field.tsx';
import HeaderAndSub from '@/modules/governmentIntake/components/shared-component.tsx';
import { useIntakeForm } from '@/modules/governmentIntake/context/intake-form-context.tsx';
import { useSelectWithOther } from '@/modules/governmentIntake/hooks/useSelectWithOther.ts';
import { createFieldChange, useFormController } from '@/modules/governmentIntake/useFormController.ts';
import { formatUsPhone, getPhoneDigits, isValidEmail, usPhoneRegex } from '@/utils';

const entryContactSchema = z
  .object({
    cityOrAgencyName: z.string().min(1, 'Name of city, municipality, county or agency is required'),
    organizationType: z.string().min(1, 'Organization type is required'),
    state: z.string().min(1, 'State is required'),
    department: z.string().min(1, 'Department is required'),
    fullName: z.string().min(1, 'Name is required'),
    jobTitle: z.string().min(1, 'Title is required'),
    email: z.string(),
    phone: z.string(),
    preferredContact: z.string().min(1, 'Preferred contact is required'),
  })
  .superRefine((data, ctx) => {
    if (data.preferredContact === 'Email') {
      const email = data.email.trim();
      if (!email) {
        ctx.addIssue({ path: ['email'], code: z.ZodIssueCode.custom, message: 'Email is required' });
      } else if (!isValidEmail(email)) {
        ctx.addIssue({ path: ['email'], code: z.ZodIssueCode.custom, message: 'Enter a valid email address (e.g. name@example.com)' });
      }
    }

    if (data.preferredContact === 'Phone') {
      const phone = data.phone.trim();
      if (!phone) {
        ctx.addIssue({ path: ['phone'], code: z.ZodIssueCode.custom, message: 'Phone number is required' });
      } else {
        const digits = getPhoneDigits(phone);
        if (digits.length !== 10) {
          ctx.addIssue({ path: ['phone'], code: z.ZodIssueCode.custom, message: 'Enter exactly 10 digits' });
        } else if (!usPhoneRegex.test(phone)) {
          ctx.addIssue({ path: ['phone'], code: z.ZodIssueCode.custom, message: 'Use US format: (555) 123-4567' });
        }
      }
    }
  });

type EntryContactFormData = z.infer<typeof entryContactSchema>;

export function SectionEntryContactInfo({ onValidationChange }: ValidationChange) {
  const { data, updateEntryContact } = useIntakeForm();

  const lastFormDataRef = useRef<string>('');
  const onValidationChangeRef = useRef(onValidationChange);
  onValidationChangeRef.current = onValidationChange;

  const initialData = useRef({
    ...data.entryContact,
    preferredContact: data.entryContact.preferredContact?.trim() || 'Email',
  }).current;

  const form = useFormController<EntryContactFormData>({
    schema: entryContactSchema,
    initialData,
  });

  const organization = useSelectWithOther(govtIntakeFormOrganizationType, (option) => option.name, data.entryContact.organizationType);

  const department = useSelectWithOther(govtIntakeFormDepartments, (option) => option.title, data.entryContact.department);

  useEffect(() => {
    const serialized = JSON.stringify(form.data);
    if (lastFormDataRef.current === serialized) return;
    lastFormDataRef.current = serialized;
    updateEntryContact(form.data);
  }, [form.data, updateEntryContact]);

  // Always report validity to parent via ref so a stale onValidationChange
  // reference never silently drops a valid=true update.
  useEffect(() => {
    onValidationChangeRef.current?.(form.isValid);
  }, [form.isValid]);

  const onChange = createFieldChange<EntryContactFormData>(form);

  const handleOrganizationTypeChange = (value: string) => {
    organization.setSelectedId(value);
    if (value !== 'other') {
      organization.setOtherValue('');
    }
    form.setField('organizationType', organization.resolveValue(value));
  };

  const handleOrganizationOtherChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    organization.setOtherValue(value);
    form.setField('organizationType', value);
  };

  const handleDepartmentChange = (value: string) => {
    department.setSelectedId(value);
    if (value !== 'other') {
      department.setOtherValue('');
    }
    form.setField('department', department.resolveValue(value));
  };

  const handleDepartmentOtherChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    department.setOtherValue(value);
    form.setField('department', value);
  };

  const handleStateChange = (value: string) => {
    form.setField('state', value);
  };

  const handlePreferredContactChange = (value: string) => {
    form.setField('preferredContact', value);
    // Clear the field that is no longer relevant so stale values don't linger
    if (value === 'Email') {
      form.setField('phone', '');
    } else {
      form.setField('email', '');
    }
  };

  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    form.setField('phone', formatUsPhone(event.target.value));
  };

  return (
    <div className='flex w-full flex-col overflow-y-auto'>
      <header className='pt-8'>
        <HeaderAndSub
          title='Organization & Contact Information'
          subTitle='Tell us about your organization and who we should follow up with.'
        />
      </header>
      <section className='grid grid-cols-1 md:grid-cols-2 gap-6 pb-4'>
        <div className='space-y-6 pt-8'>
          <HeaderAndSub title='Organization' titleClass='text-lg' />
          <FormField
            label='Name of your city, municipality, county, or agency'
            required
            hint='Please enter the full legal name of your organization.'
          >
            <Input
              value={form.data.cityOrAgencyName}
              onChange={onChange('cityOrAgencyName')}
              placeholder='eg., City of Example, Example County'
              className='w-full px-3 py-2'
            />
          </FormField>

          <FormField label='What type of organization is this' required>
            <Select value={organization.selectedId} onValueChange={handleOrganizationTypeChange}>
              <SelectTrigger className='w-full'>
                <SelectValue placeholder='Select your organization type' />
              </SelectTrigger>
              <SelectContent position='popper' side='bottom' avoidCollisions={false}>
                <SelectGroup>
                  {govtIntakeFormOrganizationType.map((organization) => (
                    <SelectItem
                      className='cursor-pointer focus:text-green-600 hover:text-green-600'
                      key={organization.id}
                      value={organization.id}
                    >
                      {organization.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {organization.isOther && (
              <div className='mt-4'>
                <FormField label='Please specify' required>
                  <Input
                    type='text'
                    value={organization.otherValue}
                    onChange={handleOrganizationOtherChange}
                    placeholder='Specify your organization type'
                    className='w-full px-3 py-2'
                  />
                </FormField>
              </div>
            )}
          </FormField>

          <FormField label='State' required>
            <Select value={form.data.state} onValueChange={handleStateChange}>
              <SelectTrigger className='w-full'>
                <SelectValue placeholder='Select state' />
              </SelectTrigger>
              <SelectContent position='popper' side='bottom' avoidCollisions={false}>
                <SelectGroup>
                  {govtIntakeFormState.map((state) => (
                    <SelectItem
                      className='cursor-pointer focus:text-green-600 hover:text-green-600'
                      key={state.name}
                      value={`${state.name}, ${state.abbreviation}`}
                    >
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </FormField>

          <FormField label='Which department are you representing?' required>
            <Select value={department.selectedId} onValueChange={handleDepartmentChange}>
              <SelectTrigger className='w-full'>
                <SelectValue placeholder='Select your department' />
              </SelectTrigger>
              <SelectContent position='popper' side='bottom' avoidCollisions={false}>
                <SelectGroup>
                  {govtIntakeFormDepartments.map((department) => (
                    <SelectItem
                      className='cursor-pointer focus:text-green-600 hover:text-green-600'
                      key={department.id}
                      value={department.id}
                    >
                      {department.title}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            <Label className='mt-1 text-xs text-gray-400'>Select the department you primarily work in.</Label>
            {department.isOther && (
              <div className='mt-4'>
                <FormField label='Please specify' required>
                  <Input
                    type='text'
                    value={department.otherValue}
                    onChange={handleDepartmentOtherChange}
                    placeholder='Specify your department'
                    className='w-full px-3 py-2'
                  />
                </FormField>
              </div>
            )}
          </FormField>
        </div>

        <div className='space-y-6 pt-8'>
          <HeaderAndSub title='Your Information' titleClass='text-lg' />
          <FormField label='Your full name' required>
            <Input
              type='text'
              value={form.data.fullName}
              onChange={onChange('fullName')}
              placeholder='First and Last Name'
              className='w-full px-3 py-2'
            />
          </FormField>
          <FormField label='Job title' required>
            <Input
              type='text'
              value={form.data.jobTitle}
              onChange={onChange('jobTitle')}
              placeholder='e.g., Director of Planning, Building Inspector'
              className='w-full px-3 py-2'
            />
          </FormField>

          <FormField label='Preferred contact method' required>
            <RadioGroup value={form.data.preferredContact} onValueChange={handlePreferredContactChange}>
              {govtIntakeFormContactMethod.map((interaction) => (
                <div key={interaction.id} className='flex items-center gap-2 cursor-pointer'>
                  <RadioGroupItem value={interaction.title} id={`contact-${interaction.id}`} />
                  <label htmlFor={`contact-${interaction.id}`} className='text-sm text-gray-700 cursor-pointer'>
                    {interaction.title}
                  </label>
                </div>
              ))}
            </RadioGroup>
          </FormField>

          {form.data.preferredContact === 'Email' && (
            <FormField label='Email' required>
              <Input
                type='email'
                value={form.data.email ?? ''}
                onChange={onChange('email')}
                placeholder='your.email@cityofexample.gov'
                className='w-full px-3 py-2'
              />
            </FormField>
          )}

          {form.data.preferredContact === 'Phone' && (
            <FormField label='Phone' required>
              <Input
                type='tel'
                inputMode='numeric'
                autoComplete='tel'
                value={form.data.phone ?? ''}
                onChange={handlePhoneChange}
                placeholder='(555) 123-4567'
                className='w-full px-3 py-2'
                maxLength={14}
              />
            </FormField>
          )}
        </div>
      </section>
    </div>
  );
}
